import { useEffect, useState } from "react";
import api from "@/lib/api";
import { publicGet, publicPost } from "@/lib/publicApi";
import MarketingShell from "@/components/MarketingShell";

const CATEGORIES = ["All", "Pricing", "Onboarding", "Payments", "Technical", "Legal"];

export default function Help() {
    const [faqs, setFaqs] = useState([]);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");
    const [open, setOpen] = useState(null);
    const [ticket, setTicket] = useState({ name: "", email: "", subject: "", message: "" });
    const [ticketMsg, setTicketMsg] = useState("");

    useEffect(() => {
        const params = new URLSearchParams();
        if (q.trim()) params.set("q", q.trim());
        if (cat !== "All") params.set("category", cat);
        publicGet(`/faqs?${params.toString()}`).then(d => setFaqs(d.items || []));
    }, [q, cat]);

    // FAQPage JSON-LD — injected once when faqs arrive.
    useEffect(() => {
        const id = "help-faqpage-jsonld";
        document.getElementById(id)?.remove();
        if (!faqs.length) return;
        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        el.textContent = JSON.stringify({
            "@context": "https://schema.org", "@type": "FAQPage",
            mainEntity: faqs.slice(0, 30).map(f => ({
                "@type": "Question", name: f.question,
                acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
        });
        document.head.appendChild(el);
        return () => document.getElementById(id)?.remove();
    }, [faqs]);

    async function submitTicket(e) {
        e.preventDefault();
        setTicketMsg("");
        try {
            const r = await publicPost("/support-tickets", ticket);
            setTicketMsg(`Ticket created (#${r.ticket_id.slice(0, 8)}) — we'll respond within one working day.`);
            setTicket({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            setTicketMsg(err?.data?.detail || err?.message || "Something went wrong. Please try again.");
        }
    }

    return (
        <MarketingShell>
            <div className="max-w-4xl mx-auto py-14 px-6" data-testid="help-page">
                <div className="text-xs uppercase tracking-wider text-[#17212B]/60 mb-2">Help & FAQ</div>
                <h1 className="text-4xl sm:text-5xl font-extrabold text-[#123B5D] leading-tight">Get help</h1>
                <p className="mt-3 text-lg text-[#17212B]/70">Search the FAQ, chat with the AI assistant, or raise a support ticket.</p>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <input
                        data-testid="help-faq-search"
                        value={q} onChange={e => setQ(e.target.value)}
                        placeholder="Search FAQs…"
                        className="flex-1 border border-[#123B5D]/20 rounded-lg px-4 py-2 text-sm" />
                    <select data-testid="help-faq-category" value={cat} onChange={e => setCat(e.target.value)}
                        className="border border-[#123B5D]/20 rounded-lg px-3 py-2 text-sm">
                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                </div>

                <div className="mt-6 divide-y divide-[#123B5D]/10 border border-[#123B5D]/10 rounded-lg overflow-hidden" data-testid="help-faq-list">
                    {faqs.length === 0 && (
                        <div className="text-sm text-[#17212B]/60 p-4">No FAQs match that search.</div>
                    )}
                    {faqs.map(f => (
                        <div key={f.id} data-testid={`faq-item-${f.id}`}>
                            <button
                                type="button"
                                onClick={() => setOpen(open === f.id ? null : f.id)}
                                className="w-full text-left px-4 py-3 flex justify-between items-start hover:bg-[#F5F8FA]"
                                data-testid={`faq-toggle-${f.id}`}>
                                <span className="font-semibold text-[#17212B]">{f.question}</span>
                                <span className="text-xs text-[#17212B]/60 ml-3">{f.category}</span>
                            </button>
                            {open === f.id && (
                                <div className="px-4 pb-4 text-sm text-[#17212B]/85 leading-relaxed">{f.answer}</div>
                            )}
                        </div>
                    ))}
                </div>

                <section id="raise-ticket" className="mt-14 border-t border-[#123B5D]/10 pt-10">
                    <h2 className="text-2xl font-bold text-[#123B5D]">Raise a support ticket</h2>
                    <p className="mt-1 text-sm text-[#17212B]/60">First response within one working day.</p>
                    <form onSubmit={submitTicket} className="mt-4 space-y-3" data-testid="support-ticket-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input required data-testid="ticket-name" value={ticket.name} onChange={e => setTicket({ ...ticket, name: e.target.value })} placeholder="Your name" className="border border-[#123B5D]/20 rounded-lg px-3 py-2 text-sm" />
                            <input required type="email" data-testid="ticket-email" value={ticket.email} onChange={e => setTicket({ ...ticket, email: e.target.value })} placeholder="Email" className="border border-[#123B5D]/20 rounded-lg px-3 py-2 text-sm" />
                        </div>
                        <input required data-testid="ticket-subject" value={ticket.subject} onChange={e => setTicket({ ...ticket, subject: e.target.value })} placeholder="Subject" className="w-full border border-[#123B5D]/20 rounded-lg px-3 py-2 text-sm" />
                        <textarea required data-testid="ticket-message" value={ticket.message} onChange={e => setTicket({ ...ticket, message: e.target.value })} placeholder="How can we help?" rows="4" className="w-full border border-[#123B5D]/20 rounded-lg px-3 py-2 text-sm" />
                        {/* honeypot */}
                        <input type="text" name="website" style={{ display: "none" }} tabIndex="-1" autoComplete="off" onChange={e => setTicket({ ...ticket, website: e.target.value })} />
                        <button type="submit" data-testid="ticket-submit" className="bg-[#123B5D] text-white px-6 py-2 rounded-lg font-semibold text-sm">Submit ticket</button>
                        {ticketMsg && <div className="text-sm text-[#0F766E]" data-testid="ticket-status">{ticketMsg}</div>}
                    </form>
                </section>
            </div>
        </MarketingShell>
    );
}
